import { all, takeLeading, call, put } from '@redux-saga/core/effects';
import axios from 'axios';

import { ERROR, SUCCESS } from '../../../utils/constants';
import { getErrorMessage } from '../../../utils/api-util';

import {
  requestSendForAmendment,
  requestViewProductListings,
  requestRetrieveProductListing,
  requestUpdateProductListing,
  requestViewBuyerListings,
  requestRetrieveBuyerListing,
  requestUpdateBuyerListing,
  requestViewAccounts,
  requestApproveProduct,
} from './admin-product.actions';
import {
  getSendForAmendmentRequest,
  getViewProductListingsRequest,
  getRetrieveProductListingRequest,
  getUpdateProductListingRequest,
  getViewBuyerListingsRequest,
  getRetrieveBuyerListingRequest,
  getUpdateBuyerListingRequest,
  getViewAccountsRequest,
  getApproveProductRequest,
} from './admin-product.endpoints';

import { addSystemNotice, addSpinner, removeSpinner } from '../../../slices/system/system.slice';
import {
  setProductListings,
  setProductListing,
  setBuyerListings,
  setBuyerListing,
} from '../../../slices/product/product.slice';
import { setAccounts } from '../../../slices/party/party.slice';

export function* performSendForAmendmentRequest({ payload }) {
  const { amendment, onSuccess } = payload;

  try {
    yield put(addSpinner('Sending For Amendment'));

    const [endpoint, options] = getSendForAmendmentRequest(amendment);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    yield put(setProductListings([]));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Sending For Amendment'));
  }
}

export function* watchForSendForAmendmentRequest() {
  yield takeLeading(requestSendForAmendment.type, performSendForAmendmentRequest);
}

export function* performViewProductListingsRequest({ payload }) {
  const { filters, onSuccess } = payload;

  try {
    yield put(addSpinner('Retrieving Product Listings'));

    const [endpoint, options] = getViewProductListingsRequest(filters);

    const { data } = yield call(axios, endpoint, options);
    const { sellerListings, totalSellerListings } = data;

    const filteredListings = sellerListings.filter((listing) => {
      const category = listing.category.name;
      const classification = listing.classification.name;
      const type = listing.specification.name;
      const name = listing.brandName;
      const status = listing.status;
      const searchString = filters.name;

      const stringToFilter = `${classification} ${category} ${type} ${name} ${status}`;
      if (!searchString || stringToFilter.toLowerCase().includes(searchString.toLowerCase())) {
        return listing;
      }

      return null;
    });

    if (onSuccess) {
      yield call(onSuccess, totalSellerListings);
    }

    yield put(setProductListings(filteredListings));
  } catch (error) {
    yield put(setProductListings([]));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Product Listings'));
  }
}

export function* watchForViewProductListingsRequest() {
  yield takeLeading(requestViewProductListings.type, performViewProductListingsRequest);
}

export function* performRetrieveProductListingRequest({ payload }) {
  const { productExternalReference } = payload;

  try {
    yield put(addSpinner('Retrieving Product Listing'));

    const [endpoint, options] = getRetrieveProductListingRequest(productExternalReference);

    const { data } = yield call(axios, endpoint, options);

    yield put(setProductListing(data.sellerListingVO));
  } catch (error) {
    yield put(setProductListing(undefined));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Product Listing'));
  }
}

export function* watchForRetrieveProductListingRequest() {
  yield takeLeading(requestRetrieveProductListing.type, performRetrieveProductListingRequest);
}

export function* performUpdateProductListingRequest({ payload }) {
  const { productListing, showToaster = true, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating Product Listing'));

    const [endpoint, options] = getUpdateProductListingRequest(productListing);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    if (showToaster) {
      yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Updating Product Listing'));
  }
}

export function* watchForUpdateProductListingRequest() {
  yield takeLeading(requestUpdateProductListing.type, performUpdateProductListingRequest);
}

export function* performViewBuyerListingsRequest({ payload }) {
  const { filters, onSuccess } = payload;

  try {
    yield put(addSpinner('Retrieving Buyer Listings'));

    const [endpoint, options] = getViewBuyerListingsRequest(filters);

    const { data } = yield call(axios, endpoint, options);
    const { buyerListingVOS, totalBuyerListings } = data;

    const filteredListings = buyerListingVOS.filter((listing) => {
      const category = listing.category.name;
      const classification = listing.classification.name;
      const type = listing.specification.name;
      const name = listing.brandName;
      const status = listing.status;
      const searchString = filters.name;

      const stringToFilter = `${classification} ${category} ${type} ${name} ${status}`;
      if (!searchString || stringToFilter.toLowerCase().includes(searchString.toLowerCase())) {
        return listing;
      }

      return null;
    });

    if (onSuccess) {
      yield call(onSuccess, totalBuyerListings);
    }

    yield put(setBuyerListings(filteredListings));
  } catch (error) {
    yield put(setBuyerListings([]));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Buyer Listings'));
  }
}

export function* watchForViewBuyerListingsRequest() {
  yield takeLeading(requestViewBuyerListings.type, performViewBuyerListingsRequest);
}

export function* performRetrieveBuyerListingRequest({ payload }) {
  const { productExternalReference } = payload;

  try {
    yield put(addSpinner('Retrieving Buyer Listing'));

    const [endpoint, options] = getRetrieveBuyerListingRequest(productExternalReference);

    const { data } = yield call(axios, endpoint, options);

    yield put(setBuyerListing(data.buyerListingVO));
  } catch (error) {
    yield put(setBuyerListing(undefined));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Buyer Listing'));
  }
}

export function* watchForRetrieveBuyerListingRequest() {
  yield takeLeading(requestRetrieveBuyerListing.type, performRetrieveBuyerListingRequest);
}

export function* performUpdateBuyerListingRequest({ payload }) {
  const { buyerListing, showToaster = true, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating Buyer Listing'));

    const [endpoint, options] = getUpdateBuyerListingRequest(buyerListing);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    if (showToaster) {
      yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Updating Buyer Listing'));
  }
}

export function* watchForUpdateBuyerListingRequest() {
  yield takeLeading(requestUpdateBuyerListing.type, performUpdateBuyerListingRequest);
}

export function* performViewAccountsRequest() {
  try {
    yield put(addSpinner('Retrieving Accounts'));

    const [endpoint, options] = getViewAccountsRequest();

    const { data } = yield call(axios, endpoint, options);

    yield put(setAccounts(data.accounts));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Accounts'));
  }
}

export function* watchForViewAccountsRequest() {
  yield takeLeading(requestViewAccounts.type, performViewAccountsRequest);
}

export function* performApproveProductRequest({ payload }) {
  const { externalReference, onSuccess } = payload;

  try {
    yield put(addSpinner('Approving Product'));

    const [endpoint, options] = getApproveProductRequest(externalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Approving Product'));
  }
}

export function* watchForApproveProductRequest() {
  yield takeLeading(requestApproveProduct.type, performApproveProductRequest);
}

export default function* adminProductSaga() {
  yield all([
    watchForSendForAmendmentRequest(),
    watchForViewProductListingsRequest(),
    watchForRetrieveProductListingRequest(),
    watchForUpdateProductListingRequest(),
    watchForViewBuyerListingsRequest(),
    watchForRetrieveBuyerListingRequest(),
    watchForUpdateBuyerListingRequest(),
    watchForViewAccountsRequest(),
    watchForApproveProductRequest(),
  ]);
}
